/**
 * MonthlyForecastChart Component
 *
 * Displays a 12-month financial forecast as a bar chart showing:
 * - Monthly costs (red bars)
 * - Monthly benefits (green bars)
 * - Cumulative net value (line overlay)
 * - Payback point indicator
 */

import React from 'react';

interface MonthlyForecastEntry {
  month: number;
  calendar_month: string;
  costs: {
    total: number;
    capex: number;
    opex: number;
  };
  benefits: {
    total: number;
    capex_savings: number;
    opex_savings: number;
    revenue_increase: number;
  };
  net_value: number;
  cumulative_net: number;
}

interface ForecastTotals {
  total_cost: number;
  total_benefit: number;
  net_benefit: number;
  roi_percentage: number | null;
  payback_month: number | null;
  npv: number | null;
}

export interface MonthlyForecastChartProps {
  months: MonthlyForecastEntry[];
  totals: ForecastTotals;
  isLoading?: boolean;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Format currency helper
 */
const formatCurrency = (amount: number): string => {
  if (Math.abs(amount) >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(amount) >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount.toFixed(0)}`;
};

/**
 * Format full currency helper
 */
const formatFullCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Get month label from calendar_month string or index
 */
const getMonthLabel = (calendarMonth: string, index: number): string => {
  if (calendarMonth) {
    try {
      const date = new Date(calendarMonth);
      if (!isNaN(date.getTime())) {
        return MONTH_NAMES[date.getMonth()] || `M${index + 1}`;
      }
    } catch {
      // Fall back to index
    }
  }
  return `M${index + 1}`;
};

export const MonthlyForecastChart: React.FC<MonthlyForecastChartProps> = ({
  months,
  totals,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="h-80 flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-gray-500 dark:text-gray-400">Loading forecast...</div>
      </div>
    );
  }

  if (!months || months.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-2">No forecast data available</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Add costs and benefits to generate a forecast
          </p>
        </div>
      </div>
    );
  }

  // Calculate max values for scaling
  const maxCost = Math.max(...months.map((m) => m.costs.total), 1);
  const maxBenefit = Math.max(...months.map((m) => m.benefits.total), 1);
  const maxValue = Math.max(maxCost, maxBenefit);

  // Calculate cumulative range for the line chart
  const cumulativeValues = months.map((m) => m.cumulative_net);
  const minCumulative = Math.min(...cumulativeValues, 0);
  const maxCumulative = Math.max(...cumulativeValues, 0);
  const cumulativeRange = maxCumulative - minCumulative || 1;

  // Chart dimensions
  const chartHeight = 200;
  const barMaxHeight = chartHeight - 20;

  // Calculate bar heights
  const getBarHeight = (value: number): number => {
    return Math.max((value / maxValue) * barMaxHeight, 0);
  };

  // Calculate cumulative line Y position as percentage (0-100, inverted for SVG)
  const getCumulativeY = (value: number): number => {
    const normalized = (value - minCumulative) / cumulativeRange;
    // Return percentage (0-100) where 0 is bottom and 100 is top
    return 95 - normalized * 90; // Leave 5% margin top and bottom
  };

  // Bar width as percentage
  const barWidth = 100 / months.length;

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 text-sm">
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
          <div className="text-red-600 dark:text-red-400 text-xs font-medium uppercase">
            Year 1 Costs
          </div>
          <div className="text-red-700 dark:text-red-300 text-lg font-bold">
            {formatFullCurrency(totals.total_cost)}
          </div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
          <div className="text-green-600 dark:text-green-400 text-xs font-medium uppercase">
            Year 1 Benefits
          </div>
          <div className="text-green-700 dark:text-green-300 text-lg font-bold">
            {formatFullCurrency(totals.total_benefit)}
          </div>
        </div>
        <div
          className={`rounded-lg p-3 ${totals.net_benefit >= 0 ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-orange-50 dark:bg-orange-900/20'}`}
        >
          <div
            className={`text-xs font-medium uppercase ${totals.net_benefit >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'}`}
          >
            Net Benefit
          </div>
          <div
            className={`text-lg font-bold ${totals.net_benefit >= 0 ? 'text-blue-700 dark:text-blue-300' : 'text-orange-700 dark:text-orange-300'}`}
          >
            {formatFullCurrency(totals.net_benefit)}
          </div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
          <div className="text-purple-600 dark:text-purple-400 text-xs font-medium uppercase">
            Payback
          </div>
          <div className="text-purple-700 dark:text-purple-300 text-lg font-bold">
            {totals.payback_month ? `Month ${totals.payback_month}` : 'N/A'}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        {/* Y-axis labels */}
        <div className="absolute left-0 top-4 bottom-8 w-12 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 text-right pr-2">
          <span>{formatCurrency(maxValue)}</span>
          <span>{formatCurrency(maxValue / 2)}</span>
          <span>$0</span>
        </div>

        {/* Chart area */}
        <div className="ml-12 relative overflow-hidden" style={{ height: `${chartHeight}px` }}>
          {/* Grid lines */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            <div className="border-b border-gray-200 dark:border-gray-700" />
            <div className="border-b border-gray-200 dark:border-gray-700 border-dashed" />
            <div className="border-b border-gray-300 dark:border-gray-600" />
          </div>

          {/* Bars */}
          <div className="absolute inset-0 flex justify-around items-end pb-2.5">
            {months.map((month, index) => {
              const costHeight = getBarHeight(month.costs.total);
              const benefitHeight = getBarHeight(month.benefits.total);
              const isPaybackMonth = totals.payback_month === month.month;

              return (
                <div
                  key={index}
                  className="flex-1 flex flex-col items-center justify-end h-full relative group"
                >
                  {/* Payback indicator */}
                  {isPaybackMonth && (
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10">
                      <div className="w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                        <svg
                          className="w-2.5 h-2.5 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Bars container */}
                  <div className="flex items-end gap-0.5 h-full px-0.5">
                    {/* Cost bar */}
                    <div
                      className="w-3 bg-red-400 dark:bg-red-500 rounded-t transition-all hover:bg-red-500 dark:hover:bg-red-400"
                      style={{ height: `${costHeight}px` }}
                      title={`Cost: ${formatFullCurrency(month.costs.total)}`}
                    />
                    {/* Benefit bar */}
                    <div
                      className="w-3 bg-green-400 dark:bg-green-500 rounded-t transition-all hover:bg-green-500 dark:hover:bg-green-400"
                      style={{ height: `${benefitHeight}px` }}
                      title={`Benefit: ${formatFullCurrency(month.benefits.total)}`}
                    />
                  </div>

                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                    <div className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                      <div className="font-semibold mb-1">
                        {getMonthLabel(month.calendar_month, index)}
                      </div>
                      <div className="text-red-300 dark:text-red-600">
                        Cost: {formatFullCurrency(month.costs.total)}
                      </div>
                      <div className="text-green-300 dark:text-green-600">
                        Benefit: {formatFullCurrency(month.benefits.total)}
                      </div>
                      <div
                        className={
                          month.net_value >= 0
                            ? 'text-blue-300 dark:text-blue-600'
                            : 'text-orange-300 dark:text-orange-600'
                        }
                      >
                        Net: {formatFullCurrency(month.net_value)}
                      </div>
                      <div className="text-gray-400 dark:text-gray-500 border-t border-gray-700 dark:border-gray-300 mt-1 pt-1">
                        Cumulative: {formatFullCurrency(month.cumulative_net)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cumulative value dots */}
          {months.map((m, i) => {
            const leftPercent = (i + 0.5) * barWidth;
            const topPercent = getCumulativeY(m.cumulative_net);
            return (
              <div
                key={`dot-${i}`}
                className="absolute w-2.5 h-2.5 bg-purple-500 rounded-full border-2 border-white shadow-sm pointer-events-none"
                style={{
                  left: `${leftPercent}%`,
                  top: `${topPercent}%`,
                  transform: 'translate(-50%, -50%)',
                }}
                title={`Cumulative: ${formatFullCurrency(m.cumulative_net)}`}
              />
            );
          })}
        </div>

        {/* X-axis labels */}
        <div className="ml-12 flex justify-around mt-2">
          {months.map((month, index) => (
            <div
              key={index}
              className="flex-1 text-center text-xs text-gray-500 dark:text-gray-400"
            >
              {getMonthLabel(month.calendar_month, index)}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-6 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-red-400 dark:bg-red-500 rounded" />
          <span className="text-gray-600 dark:text-gray-400">Monthly Costs</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-green-400 dark:bg-green-500 rounded" />
          <span className="text-gray-600 dark:text-gray-400">Monthly Benefits</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-purple-500" />
          <span className="text-gray-600 dark:text-gray-400">Cumulative Net</span>
        </div>
        {totals.payback_month && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-purple-500 rounded-full flex items-center justify-center">
              <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <span className="text-gray-600 dark:text-gray-400">Payback Point</span>
          </div>
        )}
      </div>

      {/* Additional metrics */}
      <div className="grid grid-cols-2 gap-4 text-sm border-t border-gray-200 dark:border-gray-700 pt-4">
        <div>
          <span className="text-gray-500 dark:text-gray-400">ROI (Year 1):</span>
          <span
            className={`ml-2 font-semibold ${(totals.roi_percentage || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
          >
            {totals.roi_percentage !== null ? `${totals.roi_percentage.toFixed(1)}%` : 'N/A'}
          </span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">NPV (10% discount):</span>
          <span
            className={`ml-2 font-semibold ${(totals.npv || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
          >
            {totals.npv !== null ? formatFullCurrency(totals.npv) : 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MonthlyForecastChart;
